import { z } from 'zod';
import { ParticipantHeaderSchema, WithdrawalIdSchema } from './common.js';
/**
 * Observability tier 1 (2026-08-27 incident pair): PF's own view of recon-feed polling health,
 * plus intake for the ledger divergences a participant's own reconciliation already detects.
 *
 * Neither of these is on the money path — no transition, claim, or credit instruction reads
 * either table this package's schemas describe. Both exist so an operator on the PF console can
 * see what, until now, only lived on the participant's side or nowhere at all.
 */
// ---------------------------------------------------------------------------------------------
// Recon feed health (`GET /ops/v1/recon-health`) — read-only, no participant-facing schema:
// the feed poll itself (`GET /v1/withdrawals` / `GET /v1/transactions`) already carries `cursor`,
// and this table is written from the same query params, not a separate request body.
// ---------------------------------------------------------------------------------------------
/** The two recon feeds PF tracks polling health for. */
export const ReconFeedSchema = z.enum(['withdrawals', 'transactions']);
/** Traffic-light summary the console renders directly, computed server-side so the threshold rules live in one place. */
export const ReconHealthStatusSchema = z.enum(['green', 'yellow', 'red']);
/** One participant/feed's polling health. */
export const ReconHealthRowSchema = z
    .object({
    participant_id: ParticipantHeaderSchema,
    feed: ReconFeedSchema,
    last_polled_at: z.string().datetime({ offset: true }),
    /** The cursor the participant last sent, verbatim. `null` when it has only ever asked for a first page. */
    cursor_value: z.string().nullable(),
    /** That cursor's own decoded position — the keyset instant the participant is reading from. */
    cursor_position_at: z.string().datetime({ offset: true }).nullable(),
    /** The last time `cursor_value` was observed to change, not merely resent. `null` if never observed to move. */
    cursor_advanced_at: z.string().datetime({ offset: true }).nullable(),
    /** Seconds since `last_polled_at`, as of the response. */
    poll_gap_seconds: z.number().int().nonnegative(),
    /** `cursor_advanced_at` is stale AND the feed genuinely has rows past `cursor_position_at` right now. */
    stalled: z.boolean(),
    status: ReconHealthStatusSchema,
})
    .strict();
/** `GET /ops/v1/recon-health` query — `participant` narrows to one website, like every other ops list. */
export const ReconHealthQuerySchema = z.object({ participant: z.string().optional() }).strict();
/** `GET /ops/v1/recon-health` response. */
export const ReconHealthListResponseSchema = z
    .object({ items: z.array(ReconHealthRowSchema) })
    .strict();
// ---------------------------------------------------------------------------------------------
// Ledger divergence intake (`POST /v1/ledger-divergences`, `GET /ops/v1/ledger-divergences`)
// ---------------------------------------------------------------------------------------------
/**
 * One divergence a participant's own ledger detected.
 *
 * `op` and `code` are the participant's own vocabulary, not a PF-defined enum: PF's job is to
 * surface these to an operator verbatim, not to classify a ledger it does not own.
 */
export const LedgerDivergenceItemSchema = z
    .object({
    op: z.string().min(1).max(64),
    code: z.string().min(1).max(64),
    lock_reference: z.string().min(1).max(255),
    /** PF's own withdrawal id, when the participant could resolve one. Absent when it could not. */
    withdrawal_id: WithdrawalIdSchema.optional(),
    /** The participant's own clock, for when the divergence actually happened on their side. */
    occurred_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * `POST /v1/ledger-divergences` body — capped at 200 per batch, matching every other bounded
 * list this contract package caps at (`PaginationQuerySchema.limit`'s ceiling).
 */
export const LedgerDivergenceBatchRequestSchema = z
    .object({ items: z.array(LedgerDivergenceItemSchema).min(1).max(200) })
    .strict();
/**
 * `POST /v1/ledger-divergences` response — how many of `items` were newly recorded. Idempotent at
 * the database's own unique index, so a participant's retry of an already-accepted batch answers
 * with a smaller (possibly zero) count rather than a duplicate row or an error.
 */
export const LedgerDivergenceBatchResponseSchema = z
    .object({ accepted: z.number().int().nonnegative() })
    .strict();
/** One recorded divergence, as the ops list reads it. */
export const LedgerDivergenceRowSchema = z
    .object({
    id: z.string(),
    participant_id: ParticipantHeaderSchema,
    op: z.string(),
    code: z.string(),
    lock_reference: z.string(),
    withdrawal_id: z.string().nullable(),
    occurred_at: z.string().datetime({ offset: true }),
    received_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `GET /ops/v1/ledger-divergences` query — newest first, `participant` narrows like every ops list. */
export const LedgerDivergenceQuerySchema = z
    .object({
    participant: z.string().optional(),
    since: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
/** `GET /ops/v1/ledger-divergences` response. */
export const LedgerDivergenceListResponseSchema = z
    .object({ items: z.array(LedgerDivergenceRowSchema) })
    .strict();
//# sourceMappingURL=observability.js.map